// StickyShansoLearnPromoBar — slim, dismissible bottom bar promoting
// ShansoLearn courses across the marketing site. Persists dismissal per
// browser via sessionStorage so it shows once per visit. Mounted globally on
// the LandingPage (and any other marketing surface where it's imported).

import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Zap, ArrowRight, X } from "lucide-react";

const KEY = "shansolearn_promo_dismissed_v1";

export default function StickyShansoLearnPromoBar() {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    // Don't show on /courses pages — user is already there
    if (pathname.startsWith("/courses") || pathname.startsWith("/learn")) {
      return undefined;
    }
    let dismissed = false;
    try {
      dismissed = sessionStorage.getItem(KEY) === "1";
    } catch (_) {
      /* sessionStorage blocked — show anyway */
    }
    if (dismissed) return undefined;
    // Small delay so it doesn't compete with hero animation
    const t = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(t);
  }, [pathname]);

  function dismiss(e) {
    e?.stopPropagation();
    try { sessionStorage.setItem(KEY, "1"); } catch (_) { /* ignore */ }
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[55] pointer-events-none"
      data-testid="shansolearn-sticky-promo"
    >
      <div className="mx-auto max-w-5xl px-3 pb-14 sm:pb-4 pr-3 sm:pr-3 pointer-events-auto">
        <div
          className="relative flex items-center gap-3 px-3 sm:px-5 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl border backdrop-blur-xl shadow-[0_-10px_40px_-10px_rgba(0,240,255,0.35)] animate-slide-up"
          style={{
            background: "linear-gradient(90deg, rgba(2,2,16,0.95), rgba(8,28,40,0.95))",
            borderColor: "rgba(0,240,255,0.35)",
          }}
        >
          {/* Lightning badge */}
          <div
            className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center border"
            style={{ background: "rgba(163,230,53,0.15)", borderColor: "rgba(163,230,53,0.4)" }}
          >
            <Zap size={16} fill="#a3e635" className="text-[#a3e635]" />
          </div>

          {/* Copy */}
          <div className="min-w-0 flex-1">
            <p className="text-white text-[13px] sm:text-sm font-bold leading-tight">
              <span className="text-[#a3e635]">9 ShansoLearn courses</span> live · Lifetime access from{" "}
              <span className="text-[#00F0FF]">₹399</span>
            </p>
            <p className="text-zinc-400 text-[11px] sm:text-xs leading-tight mt-0.5 hidden sm:block">
              20-min modules · LinkedIn-shareable certificates · No subscriptions
            </p>
          </div>

          {/* CTA */}
          <button
            onClick={() => navigate("/courses")}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-black font-extrabold text-xs sm:text-sm whitespace-nowrap hover:scale-[1.03] transition-transform"
            style={{ background: "linear-gradient(135deg, #00F0FF, #0891b2)" }}
            data-testid="shansolearn-sticky-cta"
          >
            Browse <ArrowRight size={13} />
          </button>

          {/* Close */}
          <button
            onClick={dismiss}
            className="shrink-0 w-7 h-7 -mr-1 rounded-full flex items-center justify-center text-zinc-500 hover:text-white hover:bg-white/5 transition-colors"
            aria-label="Dismiss"
            data-testid="shansolearn-sticky-dismiss"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(120%); opacity: 0; }
          to   { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up { animation: slide-up 0.5s cubic-bezier(.2,.85,.3,1) both; }
      `}</style>
    </div>
  );
}
